import type { ReactNode } from 'react'

import { chromeMetaTagClass } from '../ChromeSurface'

type Issue = {
  level: string
  code: string
  message: string
  nodeId?: string
}

type Props = {
  angular: boolean
  issues: Issue[]
  runtime?: {
    creationQueueEnabled: boolean
    workflowWorkerAvailable: boolean
  } | null
  className?: string
  footer?: ReactNode
}

/** 工作流预检结果：运行环境与图 lint 问题列表 */
export function WorkflowPreflightPanel({ angular, issues, runtime, className = '', footer }: Props) {
  const errors = issues.filter((issue) => issue.level === 'error')
  const warnings = issues.filter((issue) => issue.level === 'warning')
  const queueDown = runtime != null && !runtime.creationQueueEnabled

  return (
    <div className={['space-y-3', className].filter(Boolean).join(' ')}>
      {queueDown ? (
        <div className="border border-red-400/60 bg-red-500/10 px-3 py-2 text-sm text-red-600">
          创作队列未启用，工作流无法执行。请检查服务端配置 glyphicai.creation.queue.enabled。
        </div>
      ) : null}
      {errors.length > 0 ? (
        <section className="space-y-2">
          <div className="flex items-center gap-2">
            <span className={chromeMetaTagClass(angular, 'outline')}>必须修复</span>
            <span className="text-xs text-ink-soft">{errors.length} 项</span>
          </div>
          <ul className="space-y-1.5 text-sm text-ink">
            {errors.map((issue) => (
              <li key={`${issue.code}-${issue.nodeId ?? ''}-${issue.message}`} className="leading-relaxed">
                {issue.message}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {warnings.length > 0 ? (
        <section className="space-y-2">
          <div className="flex items-center gap-2">
            <span className={chromeMetaTagClass(angular, 'outline', { hint: true })}>建议优化</span>
            <span className="text-xs text-ink-soft">{warnings.length} 项</span>
          </div>
          <ul className="space-y-1.5 text-sm text-ink-soft">
            {warnings.map((issue) => (
              <li key={`${issue.code}-${issue.nodeId ?? ''}-${issue.message}`} className="leading-relaxed">
                {issue.message}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {errors.length === 0 && warnings.length === 0 && !queueDown ? (
        <p className="text-sm text-ink-soft">当前图结构与运行环境检查通过，可以试运行。</p>
      ) : null}
      {footer}
    </div>
  )
}
